/**
 * StaffLoginPage — operator console sign-in.
 *
 * Wires the email/phone + password form against `service-identity` at
 * `${VITE_IDENTITY_BASE_URL}/v1/auth/login`. On 200 the access + refresh
 * tokens are persisted via `tokenStore`; on 401 we surface "Invalid email
 * or password" inline.
 */
import { type FormEvent, type JSX, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Lock, Mail } from 'lucide-react';

import { useAuth } from '../../lib/auth/useAuth';
import { setRefreshToken } from '../../lib/auth/tokenStore';
import { env } from '../../lib/env';

interface LocationState {
  from?: { pathname: string };
}

interface Touched {
  readonly email: boolean;
  readonly password: boolean;
}

export function StaffLoginPage(): JSX.Element {
  const { setToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [remember, setRemember] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [touched, setTouched] = useState<Touched>({ email: false, password: false });

  const target = (location.state as LocationState | null)?.from?.pathname ?? '/';

  const emailValid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || /^\+?\d{10,14}$/.test(email);
  const passwordValid = password.length >= 4;

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setTouched({ email: true, password: true });
    setError('');

    if (!emailValid || !passwordValid) return;

    setLoading(true);
    try {
      const res = await fetch(`${env.identityBaseUrl}/v1/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({ identifier: email, password }),
      });
      if (res.status === 401) {
        setError('Invalid email or password.');
        return;
      }
      if (!res.ok) {
        let message = `Sign-in failed (HTTP ${res.status}).`;
        try {
          const body = (await res.json()) as { message?: unknown };
          if (typeof body?.message === 'string' && body.message.length > 0) {
            message = body.message;
          }
        } catch {
          // Non-JSON body; keep the generic HTTP message.
        }
        setError(message);
        return;
      }
      const tokens = (await res.json()) as {
        accessToken?: string;
        refreshToken?: string;
        expiresIn?: number;
      };
      if (!tokens.accessToken || !tokens.refreshToken) {
        setError('Sign-in returned a malformed response. Try again.');
        return;
      }
      setToken(tokens.accessToken);
      setRefreshToken(tokens.refreshToken);
      navigate(target, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error contacting the identity service.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-xl bg-nipost-green flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-white font-bold text-2xl">N</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">UPMP</h1>
          <p className="text-muted text-sm mt-1">Unified Postal Management Platform</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-border p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Welcome back</h2>
          <p className="text-muted text-sm mb-6">Sign in to your staff account</p>

          {error && (
            <div
              className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
              role="alert"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label
                htmlFor="staff-login-email"
                className="block text-sm font-medium text-gray-700 mb-1.5"
              >
                Email or Phone
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="staff-login-email"
                  data-testid="staff-login-identifier"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  placeholder="admin@nipost.gov.ng"
                  className={`w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm transition-colors outline-none focus:ring-2 focus:ring-nipost-green/20 ${
                    touched.email && !emailValid
                      ? 'border-red-400 bg-red-50/50'
                      : touched.email && emailValid
                      ? 'border-green-400'
                      : 'border-border'
                  } focus:border-nipost-green`}
                />
              </div>
              {touched.email && !emailValid && email.length > 0 && (
                <p className="text-red-500 text-xs mt-1">Enter a valid email or phone number</p>
              )}
            </div>

            <div>
              <label
                htmlFor="staff-login-password"
                className="block text-sm font-medium text-gray-700 mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="staff-login-password"
                  data-testid="staff-login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                  placeholder="Enter your password"
                  className={`w-full pl-10 pr-11 py-2.5 rounded-lg border text-sm transition-colors outline-none focus:ring-2 focus:ring-nipost-green/20 ${
                    touched.password && !passwordValid
                      ? 'border-red-400 bg-red-50/50'
                      : touched.password && passwordValid
                      ? 'border-green-400'
                      : 'border-border'
                  } focus:border-nipost-green`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {touched.password && !passwordValid && password.length > 0 && (
                <p className="text-red-500 text-xs mt-1">Password must be at least 4 characters</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 rounded border-border text-nipost-green focus:ring-nipost-green/20 accent-nipost-green"
                />
                <span className="text-sm text-gray-600">Remember me</span>
              </label>
              <span className="text-sm text-muted" title="Staff password reset is admin-driven; contact your platform admin.">
                Forgot Password?
              </span>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 rounded-lg bg-nipost-green text-white font-medium text-sm hover:bg-nipost-green-dark transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        
      </div>
    </div>
  );
}
